import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { GET_CUSTOMERS, GET_CUSTOMER_BY_ID } from 'graphql/queries/customers'
import { Customer } from 'graphql/types'

/**
 * Hook to fetch customer data based on businessAccount name
 * Since businessAccount is a string that matches customerName (not customerID),
 * we need to search through all customers to find the matching one
 */
export const useCustomerByBusinessAccount = (businessAccount?: string | null) => {
  const [customer, setCustomer] = useState<Customer | null>(null)

  // Primary: treat businessAccount as customerID (Treo ID) and fetch directly
  const {
    data: byIdData,
    loading: byIdLoading,
    error: byIdError,
  } = useQuery(GET_CUSTOMER_BY_ID, {
    variables: { customerID: businessAccount as string, fresh: false },
    skip: !businessAccount,
    fetchPolicy: 'no-cache',
  })

  // Fallback: fetch all and match by name if needed
  const {
    data: allData,
    loading: allLoading,
    error: allError,
  } = useQuery(GET_CUSTOMERS, {
    variables: { fresh: false },
    skip: !businessAccount || !!byIdData?.getCustomerById,
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (byIdData?.getCustomerById) {
      setCustomer(byIdData.getCustomerById as Customer)
      return
    }
    if (allData?.getCustomers && businessAccount) {
      const match = allData.getCustomers.find(
        (c: Customer) => c.customerID === businessAccount || c.customerName === businessAccount || c.legalName === businessAccount
      )
      setCustomer(match || null)
    }
  }, [byIdData, allData, businessAccount])

  return {
    customer,
    loading: byIdLoading || allLoading,
    error: byIdError || allError,
  }
}

/**
 * Synchronous function to find customer from pre-fetched list
 * Use this when you already have the customers list loaded
 */
export const findCustomerByBusinessAccount = (
  customers: Customer[],
  businessAccount?: string | null
): Customer | null => {
  if (!businessAccount || !customers) return null
  
  return customers.find(
    c => c.customerName === businessAccount || c.legalName === businessAccount
  ) || null
}
